import { useEffect, useState, useRef } from "react";
import { submitSale, getPromoterSalesByMonth, getAllSKUs, requestCancelSale } from "../../firebase/services";
import { compareSales } from "../../hooks/useSalesSort";
import { useAuth } from "../../hooks/useAuth";
import { format, subMonths, lastDayOfMonth } from "date-fns";
import { useLanguage } from "../../hooks/useLanguage";
const MONTH = format(new Date(), "yyyy-MM");

// Danh sách 6 tháng gần nhất cho dropdown
const MONTH_OPTIONS = Array.from({ length: 6 }, (_, i) => format(subMonths(new Date(), i), "yyyy-MM"));

// Quy tắc chốt sổ: tháng hiện tại luôn mở; tháng liền trước mở tới hết ngày chốt sổ.
// Ngày chốt sổ lấy từ trang Cấu hình (PayrollConfigProvider gọi setClosingDay khi tải xong).
let closingDay = 5;
export const setClosingDay = (d) => { closingDay = Number(d) || 5; };
export const getClosingDay = () => closingDay;

export const isMonthEditable = (m) => {
  const now = new Date();
  const cur = format(now, "yyyy-MM");
  const prev = format(subMonths(now, 1), "yyyy-MM");
  return m === cur || (m === prev && now.getDate() <= closingDay);
};

// Gọi Gemini Vision API trực tiếp bằng fetch
const callGeminiVision = async (base64, mediaType = "image/jpeg") => {
  const res = await fetch(
    `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${process.env.REACT_APP_GEMINI_API_KEY}`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        contents: [{
          parts: [
            { inline_data: { mime_type: mediaType, data: base64 } },
            {
              text: `This is an MSI laptop bottom sticker. Read ONLY what is literally printed — do NOT use prior knowledge or guess any values.

RULE 1 — MODEL NAME:
Find the line that starts with "SKU:". Copy everything after "SKU:" and stop immediately after the first occurrence of "VN" (two uppercase letters V then N).
- "SKU:Stealth A16 AI+ A3XVGG-208VN-BBARI370..." → extract "Stealth A16 AI+ A3XVGG-208VN"
- "SKU:Katana 15 B13VEK-430VN-XXXX..." → extract "Katana 15 B13VEK-430VN"
Do not include anything after "VN".

RULE 2 — SERIAL NUMBER:
Find "S/N:" and copy the value after it exactly as printed, character by character.

Return ONLY this JSON:
{"model": "...", "serial": "..."}

Use "" if not visible. Never invent values.`,
            },
          ],
        }],
        generationConfig: {
          maxOutputTokens: 512,
          thinkingConfig: { thinkingBudget: 0 },
        },
        safetySettings: [
          { category: "HARM_CATEGORY_HARASSMENT",        threshold: "BLOCK_NONE" },
          { category: "HARM_CATEGORY_HATE_SPEECH",       threshold: "BLOCK_NONE" },
          { category: "HARM_CATEGORY_SEXUALLY_EXPLICIT", threshold: "BLOCK_NONE" },
          { category: "HARM_CATEGORY_DANGEROUS_CONTENT", threshold: "BLOCK_NONE" },
        ],
      }),
    }
  );
  if (!res.ok) throw new Error(`API error ${res.status}: ${await res.text()}`);
  const data = await res.json();
  const candidate = data.candidates?.[0];
  if (!candidate?.content) {
    throw new Error(`Gemini blocked (${candidate?.finishReason || "UNKNOWN"})`);
  }
  const text = candidate.content.parts
    .filter(p => !p.thought)
    .map(p => p.text || "")
    .join("\n")
    .trim();
  return text || "{}";
};

// Chuyển file/blob ảnh sang base64
const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// Nén + thu nhỏ ảnh (canvas) → Blob JPEG. Giảm token Gemini & tăng tốc Tesseract.
const downscaleImage = (file, maxDim = 1000, quality = 0.7) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(b => resolve(b || file), "image/jpeg", quality);
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => resolve(file);
    img.src = URL.createObjectURL(file);
  });

// Parse text tem MSI (dùng chung cho Tesseract & fallback thô)
const parseSticker = (raw) => {
  const text = (raw || "").replace(/\s+/g, " ");
  const skuM = text.match(/SKU[:\s]*([A-Za-z0-9 +\-]*?\d{3}VN)/i);
  const snM = text.match(/S\/?N[:\s]*([A-Za-z0-9]{6,})/i);
  return { model: skuM?.[1]?.trim() || "", serial: snM?.[1]?.trim() || "" };
};

// Tìm SKU khớp nhất với text trích xuất từ AI
// Chiến lược: (1) exact → (2) mã VN + keyword sản phẩm → (3) partial
const findBestMatchSKU = (aiModel, skus) => {
  if (!aiModel || !skus.length) return null;
  const ai = aiModel.toLowerCase();

  // 1. Exact match (bỏ space/dash)
  const normalize = (s) => s.toLowerCase().replace(/[\s\-_+]/g, "");
  const exact = skus.find(s => normalize(s.model) === normalize(aiModel));
  if (exact) return exact;

  // 2. Smart match: tìm mã VN (vd "208VN") và các keyword tên sản phẩm
  const vnMatch = aiModel.match(/(\d{3}VN)/i);
  const vnCode = vnMatch ? vnMatch[1].toLowerCase() : null;

  // Tách keywords có nghĩa: bỏ mã nội bộ (toàn chữ+số dài), giữ lại tên sản phẩm
  const keywords = aiModel
    .split(/[\s\-]+/)
    .filter(w => w.length >= 2 && w.length <= 8 && !/^[A-Z0-9]{6,}$/.test(w))
    .map(w => w.toLowerCase());

  if (vnCode && keywords.length) {
    const smart = skus.find(s => {
      const sm = s.model.toLowerCase();
      const hasVN = sm.includes(vnCode);
      const hasKeywords = keywords.filter(k => sm.includes(k)).length >= Math.min(2, keywords.length);
      return hasVN && hasKeywords;
    });
    if (smart) return smart;
  }

  // 3. Partial match fallback
  const partial = skus.find(s => {
    const sm = s.model.toLowerCase();
    return sm.includes(ai) || ai.includes(sm);
  });
  return partial || null;
};

// ── Mode 1: Chọn từ danh sách ──────────────────────────────
function ModeDropdown({ skus, value, onChange }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} required>
      <option value="">Chọn model...</option>
      {skus.map(s => (
        <option key={s.id} value={s.model}>{s.model}{s.isHighend ? " ★" : ""}</option>
      ))}
    </select>
  );
}

// ── Mode 2: Tìm kiếm text ──────────────────────────────────
function ModeSearch({ skus, onSelect }) {
  const [query, setQuery] = useState("");
  const filtered = query.length >= 1
    ? skus.filter(s => s.model.toLowerCase().includes(query.toLowerCase()))
    : [];

  return (
    <div>
      <input
        placeholder="Gõ tên model để tìm kiếm... (VD: Katana, Titan, GF63)"
        value={query}
        onChange={e => setQuery(e.target.value)}
        autoFocus
      />
      {query.length >= 1 && (
        <div style={{
          border: "0.5px solid rgba(0,0,0,0.12)", borderRadius: 8, marginTop: 4,
          maxHeight: 220, overflowY: "auto", background: "#fff",
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)"
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: "12px 14px", color: "#aaa", fontSize: 13 }}>
              Không tìm thấy model phù hợp
            </div>
          ) : (
            filtered.map(s => (
              <div key={s.id}
                onClick={() => { onSelect(s.model); setQuery(""); }}
                style={{
                  padding: "10px 14px", cursor: "pointer", fontSize: 13,
                  borderBottom: "0.5px solid rgba(0,0,0,0.06)",
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#f5f4f1"}
                onMouseLeave={e => e.currentTarget.style.background = ""}
              >
                <span>{s.model}</span>
                {s.isHighend && <span className="badge badge-purple">Highend ★</span>}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Mode 3: Chụp ảnh AI ────────────────────────────────────
function ModeCamera({ skus, onExtracted }) {
  const [imgFile, setImgFile] = useState(null);
  const [imgPreview, setImgPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [aiMsg, setAiMsg] = useState("");
  const cameraRef = useRef();  // input với capture=environment
  const albumRef = useRef();   // input không có capture (chọn từ album)

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImgFile(f);
    setImgPreview(URL.createObjectURL(f));
    setAiMsg("");
    // Reset input để có thể chọn lại cùng file
    e.target.value = "";
  };

  const handleScan = async () => {
    if (!imgFile) return;
    setScanning(true);
    setAiMsg("Đang nén ảnh...");
    try {
      // Nén ảnh dùng chung cho cả 2 bước
      const blob = await downscaleImage(imgFile);

      // ── Bước 1: Tesseract offline (miễn phí) ──
      setAiMsg("🔍 Nhận dạng offline (miễn phí)...");
      let model = "", serial = "", matched = null, usedAI = false;
      try {
        const Tesseract = (await import("tesseract.js")).default;
        const { data: { text } } = await Tesseract.recognize(blob, "eng", {
          logger: m => {
            if (m.status === "recognizing text") setAiMsg(`🔍 Nhận dạng offline... ${Math.round(m.progress * 100)}%`);
          },
        });
        const parsed = parseSticker(text);
        model = parsed.model;
        serial = parsed.serial;
        matched = findBestMatchSKU(model, skus);
      } catch (e) {
        // Tesseract lỗi → sẽ fallback Gemini bên dưới
      }

      // ── Bước 2: Fallback Gemini nếu offline không đủ ──
      if (!matched || !serial) {
        setAiMsg("🤖 Ảnh khó đọc, đang dùng AI...");
        const base64 = await fileToBase64(blob);
        const raw = await callGeminiVision(base64, "image/jpeg");
        const jsonMatch = raw.match(/\{[\s\S]*\}/);
        const parsed = jsonMatch ? JSON.parse(jsonMatch[0]) : {};
        const aiModel = (parsed.model || "").trim();
        const aiSerial = (parsed.serial || "").trim();
        const aiMatched = findBestMatchSKU(aiModel, skus);
        model = aiMatched?.model || aiModel || model;
        serial = aiSerial || serial;
        matched = aiMatched || matched;
        usedAI = true;
      }

      onExtracted({ model: matched?.model || model, serial, matchedSKU: matched, rawAiModel: model });

      const engine = usedAI ? "AI" : "offline (miễn phí)";
      setAiMsg(matched
        ? `✓ Nhận dạng ${engine}! Đã khớp SKU "${matched.model}"`
        : model
        ? `⚠ Nhận dạng ${engine} được "${model}" nhưng chưa khớp SKU. Hãy kiểm tra lại.`
        : "⚠ Không nhận dạng được. Hãy chụp lại rõ hơn."
      );
    } catch (err) {
      setAiMsg("Lỗi: " + err.message);
    } finally {
      setScanning(false);
    }
  };

  return (
    <div>
      {/* 2 nút chọn nguồn ảnh */}
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <button type="button" className="btn" style={{ flex: 1, justifyContent: "center" }}
          onClick={() => cameraRef.current?.click()}>
          📷 Chụp ảnh
        </button>
        <button type="button" className="btn" style={{ flex: 1, justifyContent: "center" }}
          onClick={() => albumRef.current?.click()}>
          🖼 Chọn từ album
        </button>
      </div>
      <div style={{ fontSize: 11, color: "#aaa", textAlign: "center", marginBottom: 10 }}>
        Chụp rõ nhãn tem dưới đáy máy, đủ sáng, không nghiêng
      </div>

      {/* Input camera (capture trực tiếp) */}
      <input ref={cameraRef} type="file" accept="image/*" capture="environment"
        style={{ display: "none" }} onChange={handleFile} />
      {/* Input album (không có capture — cho phép chọn từ thư viện ảnh) */}
      <input ref={albumRef} type="file" accept="image/*"
        style={{ display: "none" }} onChange={handleFile} />

      {imgPreview && (
        <div style={{ marginBottom: 10 }}>
          <img src={imgPreview} alt="preview"
            style={{ width: "100%", maxHeight: 200, objectFit: "contain", borderRadius: 8, border: "0.5px solid rgba(0,0,0,0.1)" }} />
          <button className="btn btn-primary btn-full" style={{ marginTop: 8 }}
            onClick={handleScan} disabled={scanning}>
            {scanning ? "🔍 Đang nhận dạng AI..." : "🔍 Nhận dạng bằng AI"}
          </button>
        </div>
      )}

      {aiMsg && (
        <div className={`alert ${aiMsg.startsWith("Lỗi") ? "alert-error" : aiMsg.startsWith("⚠") ? "alert-warn" : "alert-success"}`}
          style={{ fontSize: 12 }}>
          {aiMsg}
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────
export default function PromoterSales() {
  const { t } = useLanguage();
  const { user, profile } = useAuth();
  const [skus, setSKUs] = useState([]);
  const [viewMonth, setViewMonth] = useState(MONTH);
  const [allSales, setAllSales] = useState([]); // mọi trạng thái của viewMonth
  const [form, setForm] = useState({
    model: "", serial: "", saleDate: format(new Date(), "yyyy-MM-dd"),
  });
  const [mode, setMode] = useState("dropdown"); // "dropdown" | "search" | "camera"
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState("");

  const editable = isMonthEditable(viewMonth);
  const [sortKey, setSortKey] = useState("saleDate");
  const [sortDir, setSortDir] = useState("desc");
  const sortToggle = (key) => {
    if (key === sortKey) setSortDir(d => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir(key === "saleDate" ? "desc" : "asc"); }
  };
  const sortArrow = (key) => (key === sortKey ? (sortDir === "asc" ? " ▲" : " ▼") : "");
  const cmp = compareSales(sortKey, sortDir);

  const pendingSales = allSales.filter(s => s.status === "pending").sort(cmp);
  const sales = allSales.filter(s => s.status === "approved").sort(cmp);
  const rejectedSales = allSales.filter(s => s.status === "rejected").sort(cmp);
  const cancelledSales = allSales.filter(s => s.status === "cancelled").sort(cmp);

  const handleRequestCancel = async (s) => {
    const reason = window.prompt(t("sales.cancel_prompt"), "");
    if (reason === null) return; // bấm Cancel
    await requestCancelSale(s.id, reason.trim());
    loadSales();
  };

  const loadSales = () => {
    if (!user) return;
    getPromoterSalesByMonth(user.uid, viewMonth).then(setAllSales);
  };

  useEffect(() => {
    getAllSKUs().then(setSKUs);
  }, [user]);

  useEffect(() => {
    loadSales();
    // Ngày bán mặc định: hôm nay nếu là tháng hiện tại, ngược lại ngày đầu tháng đã chọn
    setForm(f => ({ ...f, saleDate: viewMonth === MONTH ? format(new Date(), "yyyy-MM-dd") : `${viewMonth}-01` }));
  }, [user, viewMonth]);

  const selectedSKU = skus.find(s => s.model === form.model);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editable) { setMsg("Lỗi: Tháng này đã chốt sổ, không thể nhập thêm"); return; }
    if (!form.model.trim()) { setMsg("Vui lòng chọn hoặc nhập model laptop"); return; }
    if (!form.serial.trim()) { setMsg("Vui lòng nhập serial number"); return; }
    setSubmitting(true);
    try {
      await submitSale({
        promoterId: user.uid,
        promoterName: profile.name,
        model: form.model.trim(),
        serial: form.serial.trim(),
        isHighend: selectedSKU?.isHighend || false,
        saleDate: form.saleDate,
        month: viewMonth,
      });
      setMsg("Đã gửi! Admin sẽ duyệt sớm ✓");
      setForm(f => ({ ...f, model: "", serial: "" }));
      loadSales();
    } catch (err) {
      setMsg("Lỗi: " + err.message);
    }
    setSubmitting(false);
    setTimeout(() => setMsg(""), 4000);
  };

  const handleAiExtracted = ({ model, serial }) => {
    setForm(f => ({ ...f, model, serial }));
  };

  const modeLabels = [
    { key: "dropdown", icon: "☰", label: t("ps.mode_list") },
    { key: "search",   icon: "🔍", label: t("ps.mode_search") },
    { key: "camera",   icon: "📷", label: t("ps.mode_camera") },
  ];

  return (
    <>
      <div className="page-header">
        <h1>{t("sales.submit_title")}</h1>
        <select value={viewMonth} onChange={e => setViewMonth(e.target.value)} style={{ width: 150 }}>
          {MONTH_OPTIONS.map(m => (
            <option key={m} value={m}>{t("x.month")} {m.slice(5)}/{m.slice(0, 4)}{m === MONTH ? " " + t("sales.current_tag") : ""}</option>
          ))}
        </select>
      </div>

      {!editable && (
        <div className="alert alert-warn" style={{ marginBottom: 14 }}>
          {t("sales.month_locked_view")}
        </div>
      )}
      {editable && viewMonth !== MONTH && (
        <div className="alert alert-info" style={{ marginBottom: 14 }}>
          ⏳ Bạn đang bổ sung doanh số tháng {viewMonth.slice(5)}/{viewMonth.slice(0, 4)}. Hạn chốt sổ: hết ngày 5/{MONTH.slice(5)}.
        </div>
      )}

      <div className="grid-2">

        {/* Form card — chỉ hiện khi tháng còn mở */}
        {editable ? (
        <div className="card">
          <div className="card-title" style={{ marginBottom: 14 }}>{t("sales.add_sale")}</div>

          {/* Mode selector */}
          <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
            {modeLabels.map(m => (
              <button key={m.key}
                type="button"
                className={`btn btn-sm ${mode === m.key ? "btn-primary" : ""}`}
                style={{ flex: 1, justifyContent: "center" }}
                onClick={() => { setMode(m.key); setForm(f => ({ ...f, model: "", serial: "" })); }}
              >
                {m.icon} {m.label}
              </button>
            ))}
          </div>

          {/* Mode panels */}
          <div className="form-group">
            <label className="form-label">
              {mode === "dropdown" ? "Chọn model laptop" :
               mode === "search"   ? "Tìm kiếm model laptop" :
                                     "Chụp ảnh nhãn laptop"}
            </label>
            {mode === "dropdown" && (
              <ModeDropdown skus={skus} value={form.model}
                onChange={model => setForm(f => ({ ...f, model }))} />
            )}
            {mode === "search" && (
              <ModeSearch skus={skus}
                onSelect={model => setForm(f => ({ ...f, model }))} />
            )}
            {mode === "camera" && (
              <ModeCamera skus={skus} onExtracted={handleAiExtracted} />
            )}
          </div>

          {/* Divider khi camera mode để tách phần xác nhận */}
          {mode === "camera" && (form.model || form.serial) && (
            <div style={{ borderTop: "0.5px solid rgba(0,0,0,0.08)", margin: "4px 0 14px", paddingTop: 14 }}>
              <div style={{ fontSize: 12, color: "#888", marginBottom: 10, fontWeight: 500 }}>
                ✏ Kiểm tra và chỉnh sửa thông tin nếu cần:
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Confirm fields — luôn hiện, AI sẽ pre-fill */}
            <div className="form-group">
              <label className="form-label">
                Model laptop {form.model && selectedSKU && (
                  <span className={`badge ${selectedSKU.isHighend ? "badge-purple" : "badge-green"}`} style={{ marginLeft: 6 }}>
                    {selectedSKU.isHighend ? "Highend ★" : "Standard"}
                  </span>
                )}
              </label>
              <input
                value={form.model}
                onChange={e => setForm(f => ({ ...f, model: e.target.value }))}
                placeholder="Model sẽ được điền tự động hoặc nhập tay"
                style={{ fontWeight: form.model ? 500 : 400 }}
                required
              />
              {form.model && !selectedSKU && (
                <div style={{ fontSize: 11, color: "#854F0B", marginTop: 4 }}>
                  ⚠ Model này chưa có trong danh sách SKU — vẫn có thể gửi, admin sẽ xác nhận
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Serial number</label>
              <input
                value={form.serial}
                onChange={e => setForm(f => ({ ...f, serial: e.target.value }))}
                placeholder="VD: SN20250513A001"
                style={{ fontFamily: "monospace" }}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Ngày bán</label>
              <input type="date" value={form.saleDate}
                min={`${viewMonth}-01`}
                max={format(lastDayOfMonth(new Date(viewMonth + "-01")), "yyyy-MM-dd")}
                onChange={e => setForm(f => ({ ...f, saleDate: e.target.value }))} />
            </div>

            {msg && (
              <div className={"alert " + (msg.startsWith("Lỗi") ? "alert-error" : "alert-success")}
                style={{ marginBottom: 10 }}>{msg}</div>
            )}

            <button className="btn btn-primary btn-full" type="submit" disabled={submitting}>
              {submitting ? t("sales.submitting") : t("sales.submit_btn")}
            </button>
          </form>
        </div>
        ) : (
          <div className="card" style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center", color: "#888", minHeight: 200 }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>🔒</div>
            <div style={{ fontWeight: 500 }}>{t("sales.locked_title")}</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>{t("sales.locked_sub")}</div>
          </div>
        )}

        {/* Sales history */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">{t("sales.history")}</div>
            <div style={{ fontSize: 12, color: "#888" }}>
              {pendingSales.length > 0 && (
                <span className="badge badge-amber">{pendingSales.length} {t("sales.pending_badge")}</span>
              )}
              {sales.length > 0 && (
                <span className="badge badge-green" style={{ marginLeft: 6 }}>{sales.length} {t("sales.approved_badge")}</span>
              )}
              {rejectedSales.length > 0 && (
                <span className="badge badge-red" style={{ marginLeft: 6 }}>{rejectedSales.length} {t("sales.rejected_badge")}</span>
              )}
            </div>
          </div>
          <table className="table-card">
            <thead><tr>
              <th style={{ cursor: "pointer" }} onClick={() => sortToggle("model")}>{t("sales.model")}{sortArrow("model")}</th>
              <th>{t("sales.serial")}</th>
              <th style={{ cursor: "pointer" }} onClick={() => sortToggle("saleDate")}>{t("sales.sale_date")}{sortArrow("saleDate")}</th>
              <th>{t("x.status")}</th>
            </tr></thead>
            <tbody>
              {/* Pending trước */}
              {pendingSales.map(s => (
                <tr key={s.id}>
                  <td data-label="Model" style={{ fontSize: 12 }}>{s.model}</td>
                  <td data-label="Serial" style={{ fontFamily: "monospace", fontSize: 11 }}>{s.serial}</td>
                  <td data-label="Ngày bán" style={{ fontSize: 12, color: "#888" }}>{s.saleDate?.slice(8)}/{s.saleDate?.slice(5,7)}</td>
                  <td data-label="Trạng thái"><span className="badge badge-amber">{t("sales.status_pending2")}</span></td>
                </tr>
              ))}
              {/* Approved */}
              {sales.map(s => (
                <tr key={s.id}>
                  <td data-label="Model" style={{ fontSize: 12 }}>{s.model}</td>
                  <td data-label="Serial" style={{ fontFamily: "monospace", fontSize: 11 }}>{s.serial}</td>
                  <td data-label="Ngày bán" style={{ fontSize: 12, color: "#888" }}>{s.saleDate?.slice(8)}/{s.saleDate?.slice(5,7)}</td>
                  <td data-label="Trạng thái">
                    {s.cancelRequested ? (
                      <span className="badge badge-amber">{t("sales.cancel_pending2")}</span>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        <span className="badge badge-green">{t("sales.status_approved2")}</span>
                        {editable && (
                          <button className="btn btn-sm btn-danger" style={{ fontSize: 11, padding: "2px 6px" }}
                            onClick={() => handleRequestCancel(s)}>{t("sales.request_cancel")}</button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {/* Rejected */}
              {rejectedSales.map(s => (
                <tr key={s.id}>
                  <td data-label="Model" style={{ fontSize: 12 }}>{s.model}</td>
                  <td data-label="Serial" style={{ fontFamily: "monospace", fontSize: 11 }}>{s.serial}</td>
                  <td data-label="Ngày bán" style={{ fontSize: 12, color: "#888" }}>{s.saleDate?.slice(8)}/{s.saleDate?.slice(5,7)}</td>
                  <td data-label="Trạng thái"><span className="badge badge-red">{t("sales.status_rejected2")}</span></td>
                </tr>
              ))}
              {/* Cancelled cuối */}
              {cancelledSales.map(s => (
                <tr key={s.id}>
                  <td data-label="Model" style={{ fontSize: 12 }}>{s.model}</td>
                  <td data-label="Serial" style={{ fontFamily: "monospace", fontSize: 11 }}>{s.serial}</td>
                  <td data-label="Ngày bán" style={{ fontSize: 12, color: "#888" }}>{s.saleDate?.slice(8)}/{s.saleDate?.slice(5,7)}</td>
                  <td data-label="Trạng thái"><span className="badge badge-gray">{t("sales.status_cancelled2")}</span></td>
                </tr>
              ))}
              {allSales.length === 0 && (
                <tr><td colSpan={4} style={{ color: "#aaa", textAlign: "center", padding: 20 }}>
                  {t("sales.no_history")}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
